// Catalog.ID Client — `bitcash-auth-token`: a spending token, on request
// ============================================================================
// The purpose that replaces the paste box (§4.10, §4.12, §4.13, §8). A module
// that needs to pay for something — a CPR write, an EFS part, a mail
// whitelisting — asks the CID tab for an AUTH token instead of asking the
// member to open a wallet manager and copy one across. What comes back is a
// bounded, revocable authorisation on one of the member's wallets, wrapped to
// the requester's ephemeral key. The wallet KEY never moves: nothing in this
// file releases it. (Releasing wallet keys is a different mode with a dialog of
// its own — `bitcash-wallets-release`, for the bitcash manager alone.)
//
// TWO SERVICE PATHS, and the difference between them is the whole design:
//
//   (a) `scope.payee === "operator"` — the shared DEFAULT token (D6, as amended
//       by apps/docs/plans/second-node-and-bitcash-open-issues.md §2.1, §9.3).
//       One token, minted at registration, UNBOUND — it names no payee, so it
//       pays every Operator's CPR and EFS — and scoped to Catalog.ID's service
//       types (`Voucher:DefaultToken:ServiceTypes`), with the D27 caps and no
//       expiry. Both terms are the NODE's, never the requester's. It is safe to
//       share because on the v2 ledger a token pays only an active Operator's
//       wallet (never a P2P one), every Operator is a division of one company,
//       and its service types keep it from paying content. It is cached on the
//       device (D12) with the scope it was issued with, so the member is not
//       asked anything on every request; a cold cache is refilled from the
//       wallet's active `catalog:default` token — adopted when it covers the
//       configured types, WIDENED in place (`token/update`, same code) when it
//       lacks one, REVOKED and replaced when it is still bound to a receive
//       address (a token from before §9.3) — or by minting it once.
//
//   (b) any other payee — a token for THIS module, labelled `catalog:<moduleId>`,
//       bound to the receive address the module named, minted with the scope
//       the module asked for and the caps the member saw. No cache, ever: every
//       one of these costs a consent dialog naming the scope, which is the
//       point — a token that pays someone else is a standing instruction to move
//       the member's money to them.
//
// THE TRAP THIS FILE EXISTS TO CLOSE (§10, as amended): **a module never obtains
// an unbound token with its own scope**. `scope.payee` is REQUIRED and a request
// without it is refused. The only unbound token issued here is (a), always with
// Catalog.ID's scope; an unbound token with no service types would pay content
// at any site, so a node with caps but no types issues no default token at all.
//
// Gate stack, in order:
//   1. hub stamp — `_senderServiceId` must equal `requesterId`. (Registration
//      and the module-identity signature are `client.ts`'s gates, run before
//      this handler is reached, exactly as they are for `release` and `save`.)
//   2. the §4.10 shape: purpose, key id, mode, interaction, ephemeral key.
//   3. the scope: payee REQUIRED and well-formed, caps positive for a
//      third-party payee, sites/service types normalised, expiry (if any) one day
//      to ten years ahead, as the ledger takes it.
//   4. the wallet picker's plan, and the standing-grant short-circuit.
//   5. CONSENT — unless a standing grant already covers the chosen wallet.
//   6. the cache (operator payee only), unless the module reports its last
//      token was refused, or the entry's scope no longer covers the configured
//      service types.
//   7. the wallet key — the account's wallet document for an account wallet
//      (cid-encrypted-storage §7.2), the org key wraps for the organization's
//      (§4.13). Both are PIN-level since D5 was relaxed on 2026-09-17; neither
//      prompts.
//   8. the default token: adopt, widen or replace it, or mint one; a module's
//      own: adopt the labelled active token, or mint one.
//   9. refill the cache (operator payee only) and release, ECDH-wrapped.

import {
    BITCASH_AUTH_TOKEN_PURPOSE,
    type BitcashAuthTokenRequest, type BitcashAuthTokenResponse, type BitcashTokenScope,
    type BitcashDispatchDeps, type BitcashWalletChoice, type BitcashOrgWalletOffer,
} from './bitcash-types';
import {
    SERVICE_TYPE_PATTERN, areServiceTypes, isTokenExpiryInRange, type BitcashWorkerApi, type WorkerAuthToken,
} from './bitcash-worker-client';
import {
    cachedTokenServes, decodeCachedToken, encodeCachedToken, defaultTokenSlot, orgDefaultTokenSlot,
} from './bitcash-token-cache';
import type { DefaultTokenTerms } from '../auth/payment-flow';
import {
    BITCASH_WALLET_KEY_ID, BITCASH_WALLET_LABEL_MAX,
} from '../crypto/bitcash-wallet-entry';
import type { WalletSlot } from '../crypto/bitcash-wallets-doc';
import { verifyReceiveAddress } from '../crypto/bitcash';

export { BITCASH_AUTH_TOKEN_PURPOSE };

/** The shared default token's label (D6): unbound, Catalog.ID's scope (§9.3). */
export const DEFAULT_TOKEN_LABEL = 'catalog:default';


/** One module's own token label (D6). */
export function moduleTokenLabel(requesterId: string): string {
    return `catalog:${requesterId}`;
}

/** Longest site / service-type list this handler will pass on. Beyond this the
 *  dialog stops being readable, and an unreadable dialog is not consent. */
const MAX_SCOPE_LIST = 32;
const MAX_SITE_LENGTH = 253;

/** A wallet ref, as the pilot's walletRef payee carried one: 64 lowercase hex (a v2 ref has the same shape). */
const WALLET_REF_PATTERN = /^[0-9a-f]{64}$/;

// ── parse ───────────────────────────────────────────────────────────────────

export interface ParsedBitcashScope {
    payee: string;
    isOperatorPayee: boolean;
    sites: string[];
    serviceTypes: string[];
    dailyCap: number | null;
    monthlyCap: number | null;
    expiresAt: number | null;
}

export type BitcashRequestParse =
    | { ok: true; scope: ParsedBitcashScope; walletLabel?: string }
    | { ok: false; status: 'denied' | 'error'; reason: string };

/**
 * Validate a `bitcash-auth-token` request against §4.10. Pure.
 *
 * Refusals split into two vocabularies on purpose: a missing payee is a
 * `denied` — the member's side refuses to issue it, and the module can say so
 * to its user — while a malformed envelope is an `error`, a protocol bug the
 * module's author must fix.
 */
export function parseBitcashAuthTokenRequest(
    request: BitcashAuthTokenRequest, now: number = Date.now(),
): BitcashRequestParse {
    if (request.purpose !== BITCASH_AUTH_TOKEN_PURPOSE) {
        return { ok: false, status: 'error', reason: 'purpose-mismatch' };
    }
    // The key id and the mode ARE signature-committed (`verify.ts`), so pinning
    // them here binds the member's signed envelope to the bitcash slot: a
    // request signed for `xrpl_master` can never be re-read as a token request.
    if (request.keyId !== BITCASH_WALLET_KEY_ID) {
        return { ok: false, status: 'error', reason: 'key-id-mismatch' };
    }
    if (request.mode !== 'release') {
        return { ok: false, status: 'error', reason: 'mode-not-supported' };
    }
    // Every path here shows a dialog, so the sender MUST have asked the hub to
    // focus this tab BEFORE delivery — which is exactly what the flag still
    // means after M2c. Kept as a refusal rather than folded into the universal
    // unlock redirect (§10 item 5): the redirect answers "the bag is locked",
    // while this answers "your envelope is malformed for a mode that always
    // prompts". A caller that fixes the flag gets the dialog either way.
    if (request.requiresUserInteraction !== true) {
        return { ok: false, status: 'error', reason: 'requires-user-interaction' };
    }
    if (typeof request.ephemeralPubkey !== 'string' || request.ephemeralPubkey === '') {
        return { ok: false, status: 'error', reason: 'missing-ephemeral-pubkey' };
    }
    if (request.requestedAt !== undefined && typeof request.requestedAt !== 'string') {
        return { ok: false, status: 'error', reason: 'bad-requested-at' };
    }
    if (request.walletLabel !== undefined
        && (typeof request.walletLabel !== 'string' || request.walletLabel.length > BITCASH_WALLET_LABEL_MAX)) {
        return { ok: false, status: 'error', reason: 'bad-wallet-label' };
    }

    const scope: BitcashTokenScope | undefined = request.scope;
    const payee = typeof scope?.payee === 'string' ? scope.payee.trim() : '';
    // D6's trap, and the reason this whole function exists.
    if (!payee) {
        return { ok: false, status: 'denied', reason: 'scope-payee-required' };
    }
    const isOperatorPayee = payee === 'operator';
    // On the v2 ledger a token pays a receive address of an Operator wallet
    // (apps/bitcash/docs/plans/bitcash-v2.md D52): the pilot's walletRef payee no longer exists,
    // and saying so is the member side declining, not a malformed envelope.
    if (!isOperatorPayee && !verifyReceiveAddress(payee) && WALLET_REF_PATTERN.test(payee)) {
        return { ok: false, status: 'denied', reason: 'scope-payee-wallet-ref' };
    }
    if (!isOperatorPayee && !verifyReceiveAddress(payee)) {
        return { ok: false, status: 'error', reason: 'scope-payee-malformed' };
    }
    // A FUND-shaped string that fails its checksum is a typo or a substitution,
    // never a walletRef — catching it here keeps it out of the dialog.
    if (!isOperatorPayee && /^FUND-/i.test(payee) && !verifyReceiveAddress(payee)) {
        return { ok: false, status: 'error', reason: 'scope-payee-malformed' };
    }

    const sites = normaliseList(scope?.sites, s => s.toLowerCase());
    if (sites === null) return { ok: false, status: 'error', reason: 'scope-sites-malformed' };
    if (sites.some(s => s.length > MAX_SITE_LENGTH)) {
        return { ok: false, status: 'error', reason: 'scope-sites-malformed' };
    }
    // The ledger's bare service types (D55), lower case and each once — what a
    // token's `caps.serviceTypes` takes. The pilot's EFS_INGEST / REGISTRY_WRITE
    // are not service types: a token scoped to one could pay for nothing, and
    // the member would have consented to a scope the ledger refuses.
    const listed = normaliseList(scope?.serviceTypes, s => s.toLowerCase());
    if (listed === null || listed.some(t => !SERVICE_TYPE_PATTERN.test(t))) {
        return { ok: false, status: 'error', reason: 'scope-service-types-malformed' };
    }
    const serviceTypes = [...new Set(listed)];

    const dailyCap = optionalCap(scope?.dailyCap);
    const monthlyCap = optionalCap(scope?.monthlyCap);
    if (dailyCap === undefined || monthlyCap === undefined) {
        return { ok: false, status: 'error', reason: 'scope-caps-malformed' };
    }
    // The default token's caps are the OPERATOR's (D27) — the requester's are
    // shown for information only — so only a third-party payee must name them.
    if (!isOperatorPayee && (dailyCap === null || monthlyCap === null)) {
        return { ok: false, status: 'error', reason: 'scope-caps-required' };
    }

    const rawExpiry = scope?.expiresAt;
    let expiresAt: number | null = null;
    if (rawExpiry !== undefined && rawExpiry !== null) {
        if (typeof rawExpiry !== 'number' || !Number.isFinite(rawExpiry) || rawExpiry <= 0) {
            return { ok: false, status: 'error', reason: 'scope-expiry-malformed' };
        }
        if (rawExpiry <= now) {
            return { ok: false, status: 'error', reason: 'scope-expiry-in-the-past' };
        }
        // The ledger takes one day to ten years ahead and refuses any other
        // expiry (400): refused here, before the member is asked to consent to a
        // token that cannot exist. For every payee, like the check above: the
        // default token is minted without one, but a module asking for an
        // expiry no token can carry has a bug either way.
        if (!isTokenExpiryInRange(rawExpiry, now)) {
            return { ok: false, status: 'error', reason: 'scope-expiry-out-of-range' };
        }
        expiresAt = Math.floor(rawExpiry);
    }

    return {
        ok: true,
        scope: { payee, isOperatorPayee, sites, serviceTypes, dailyCap, monthlyCap, expiresAt },
        walletLabel: request.walletLabel,
    };
}

function normaliseList(value: unknown, map: (s: string) => string): string[] | null {
    if (value === undefined || value === null) return [];
    if (!Array.isArray(value)) return null;
    if (value.length > MAX_SCOPE_LIST) return null;
    const out: string[] = [];
    for (const raw of value) {
        if (typeof raw !== 'string') return null;
        const trimmed = map(raw.trim());
        if (trimmed) out.push(trimmed);
    }
    return out;
}

function optionalCap(value: unknown): number | null | undefined {
    if (value === undefined || value === null) return null;
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined;
    return Math.floor(value);
}

// ── the wallet picker ───────────────────────────────────────────────────────

export interface WalletChoicePlanInput {
    /** The account's ACTIVE wallets, read from its `cid/bitcash-wallets`
     *  document (cid-encrypted-storage §7.2). Empty when the tab is locked or
     *  the account has no document yet. */
    wallets: readonly WalletSlot[];
    orgWallet: BitcashOrgWalletOffer | null;
    /** The module's preference, matched against a wallet's label. */
    walletLabel?: string;
}

/**
 * The picker's rows, in the order the dialog shows them; the first is the
 * default. Pure.
 *
 * AN EMPTY LIST IS STILL POSSIBLE, and it no longer means what it used to. Until
 * 2026-09-17 it was the NORMAL case — a wallet key was never device-stored (D5),
 * so a PIN-unlocked session held nothing and the picker offered one PROVISIONAL
 * row named from the plan. Now the list is a blob readable on any unlocked tab,
 * so an empty list means the tab is locked, the account predates the blob, or
 * its registration-wallet write failed. The provisional row is kept for exactly
 * those cases: the member may well hold a funded slot-0 wallet this tab cannot
 * see right now, and offering it under a neutral name beats telling them they
 * have no wallet.
 */
export function planWalletChoices(input: WalletChoicePlanInput): BitcashWalletChoice[] {
    const accounts: BitcashWalletChoice[] = input.wallets.map(wallet => ({
        id: `account:${wallet.keyIndex}`,
        kind: 'account' as const,
        keyIndex: wallet.keyIndex,
        label: wallet.label || defaultAccountLabel(wallet.keyIndex),
    }));
    accounts.sort((a, b) => (a.keyIndex ?? 0) - (b.keyIndex ?? 0));

    const choices: BitcashWalletChoice[] = accounts.length > 0
        ? accounts
        : [{ id: 'account:0', kind: 'account', keyIndex: 0, label: defaultAccountLabel(0), provisional: true }];

    if (input.orgWallet) {
        choices.push({
            id: `org:${input.orgWallet.orgCid}`,
            kind: 'org',
            orgCid: input.orgWallet.orgCid,
            label: `${input.orgWallet.label} — organization wallet`,
        });
    }

    // A named preference moves that wallet to the front; it never selects one.
    const wanted = input.walletLabel?.trim().toLowerCase();
    if (wanted) {
        const idx = choices.findIndex(c => c.label.trim().toLowerCase() === wanted);
        if (idx > 0) {
            const [picked] = choices.splice(idx, 1);
            choices.unshift(picked!);
        }
    }
    return choices;
}

function defaultAccountLabel(keyIndex: number): string {
    return keyIndex === 0 ? 'Registration wallet' : `Wallet ${keyIndex}`;
}

/**
 * The grant-policy key for one wallet. Per WALLET, not per key id: "Allow
 * always" for this module on the registration wallet must not silently cover
 * the organization's, whose money is not the member's own.
 */
export function policyKeyIdForChoice(choice: BitcashWalletChoice): string {
    return choice.kind === 'org'
        ? `${BITCASH_WALLET_KEY_ID}#org:${choice.orgCid}`
        : `${BITCASH_WALLET_KEY_ID}#${choice.keyIndex ?? 0}`;
}

// ── token matching ──────────────────────────────────────────────────────────

/**
 * The wallet's live token under `label`, or null. Revoked and expired tokens are
 * skipped — adopting either would hand a module something the Worker refuses,
 * and the module would report a 403 and come back with
 * `previousTokenRejected`, costing the member a second prompt for nothing.
 *
 * When both the request and the token name a payee ref, they must AGREE: a
 * token labelled for this module but bound to a different payee pays nobody the
 * module is asking about, and reusing it would fail at the first debit.
 */
export function pickActiveToken(
    tokens: readonly WorkerAuthToken[], label: string, now: number, payeeFund?: string,
): WorkerAuthToken | null {
    for (const token of tokens) {
        if (token.label !== label) continue;
        // A token whose code this account never kept (v2 returns none, T2) can be revoked, never handed out.
        if (!token.authToken) continue;
        if (token.revokedAt) continue;
        if (token.expiresAt !== null && token.expiresAt !== undefined && token.expiresAt <= now) continue;
        if (payeeFund && token.payee && token.payee !== payeeFund) continue;
        return token;
    }
    return null;
}

/** True when the token is bound to a payee: the ledger's `payeeRef` when the listing gave one,
 *  else the account's own note. */
export function isBoundToken(token: WorkerAuthToken): boolean {
    if (token.payeeRef !== undefined) return token.payeeRef !== null;
    return typeof token.payee === 'string' && token.payee !== '';
}

/** What the handler does with the wallet's default token (§2.3 item 4). */
export type DefaultTokenPlan =
    /** Unbound, and its service types cover the configured list: hand it out unchanged. */
    | { action: 'adopt'; token: WorkerAuthToken }
    /** Unbound, but missing a configured type: `token/update` with the caps in full, same code. */
    | { action: 'widen'; token: WorkerAuthToken }
    /** Bound to a receive address (from before §9.3): `token/update` cannot remove a payee, so
     *  revoke these and mint an unbound one (one `fee:auth`). */
    | { action: 'replace'; revoke: WorkerAuthToken[] }
    /** None live: mint it. */
    | { action: 'mint' };

/**
 * Decide, purely, what becomes of the wallet's `catalog:default`. Only tokens whose code this
 * account kept are considered — the only ones it can hand out, widen (`token/update` needs the
 * code) or revoke by code; another device's or delegate's carries no label here at all. An
 * unbound one wins over a bound one: it is the token the configuration asks for.
 */
export function planDefaultToken(
    tokens: readonly WorkerAuthToken[], serviceTypes: readonly string[], now: number,
): DefaultTokenPlan {
    const live = tokens.filter(t => t.label === DEFAULT_TOKEN_LABEL && t.authToken && !t.revokedAt
        && (t.expiresAt === null || t.expiresAt === undefined || t.expiresAt > now));
    const unbound = live.find(t => !isBoundToken(t));
    if (unbound) {
        const held = new Set(unbound.serviceTypes);
        return serviceTypes.every(t => held.has(t)) ? { action: 'adopt', token: unbound } : { action: 'widen', token: unbound };
    }
    return live.length > 0 ? { action: 'replace', revoke: live } : { action: 'mint' };
}

// ── the handler ─────────────────────────────────────────────────────────────

type ResponseBase = Pick<BitcashAuthTokenResponse, 'type' | 'to' | 'requestId'>;

function refuse(
    base: ResponseBase, status: 'denied' | 'error', reason: string,
): BitcashAuthTokenResponse {
    return { ...base, status, reason };
}

export async function handleBitcashAuthTokenRequest(
    request: BitcashAuthTokenRequest,
    base: ResponseBase,
    cid: string,
    deps: BitcashDispatchDeps,
): Promise<BitcashAuthTokenResponse> {
    const now = deps.now ?? (() => Date.now());

    // Gate 1. Defence in depth behind `client.ts`'s own stamp check: this core
    // is reachable from a test and from any future dispatcher, and a handler
    // that releases money must not depend on a caller's diligence.
    if (!request._senderServiceId || request._senderServiceId !== request.requesterId) {
        return refuse(base, 'error', 'sender-mismatch');
    }

    // Gates 2-3.
    const parsed = parseBitcashAuthTokenRequest(request, now());
    if (!parsed.ok) return refuse(base, parsed.status, parsed.reason);
    const scope = parsed.scope;

    const worker = deps.worker;
    if (!worker) return refuse(base, 'error', 'worker-unavailable');
    const wrap = deps.wrapForRequester;
    if (!wrap) return refuse(base, 'error', 'wrap-unavailable');

    // Gate 4.
    const orgWallet = deps.getOrgWalletOffer ? await deps.getOrgWalletOffer() : null;
    const choices = planWalletChoices({
        // An await now: the list is a blob read, not an in-memory bag filter
        // (cid-encrypted-storage §7.2).
        wallets: deps.listAccountWallets ? await deps.listAccountWallets() : [],
        orgWallet,
        walletLabel: parsed.walletLabel,
    });

    const tokenLabel = scope.isOperatorPayee ? DEFAULT_TOKEN_LABEL : moduleTokenLabel(request.requesterId);
    const defaultTerms = scope.isOperatorPayee ? await defaultTokenTerms(deps) : null;
    // A node that names no default-token terms — caps AND service types — issues no
    // default token (M12, D15; §9.3 D-a: never an unscoped unbound one).
    if (scope.isOperatorPayee && defaultTerms === null) return refuse(base, 'denied', 'default-token-unconfigured');
    const caps = defaultTerms
        ? { dailyCap: defaultTerms.dailyCap, monthlyCap: defaultTerms.monthlyCap }
        : { dailyCap: scope.dailyCap as number, monthlyCap: scope.monthlyCap as number };

    // Gate 5: consent, unless a standing grant already names one of these
    // wallets for this module.
    //
    // WHAT A STANDING GRANT COVERS, and what it does not. For the
    // default token it covers the whole request: that authorisation is the
    // same every time — Catalog services at any operator, the node's service
    // types and caps (D27), no expiry — so "Allow always" means something
    // stable a member can hold in their head, and skipping the dialog is
    // exactly what they asked for. For any other payee the scope is whatever
    // the module asks for on THIS request, and a grant given for a 500-bit
    // daily cap must never quietly serve a 9 000 000-bit one — so the grant
    // settles only WHICH WALLET, and the dialog still names the scope. That
    // costs nothing the member was not already paying: a third-party token
    // shows a dialog on every request regardless.
    const checkPolicy = deps.checkPolicy;
    let preApproved: BitcashWalletChoice | null = null;
    if (checkPolicy) {
        preApproved = choices.find(
            c => checkPolicy(cid, request.requesterId, policyKeyIdForChoice(c)) !== 'none') ?? null;
    }
    let chosen: BitcashWalletChoice | null = preApproved && scope.isOperatorPayee ? preApproved : null;

    if (!chosen) {
        if (!deps.confirmBitcashAuthToken) {
            return refuse(base, 'error', 'consent-ui-unavailable');
        }
        const decision = await deps.confirmBitcashAuthToken({
            requesterId: request.requesterId,
            requesterLabel: request.requesterLabel,
            owningCid: cid,
            choices: preApproved ? [preApproved] : choices,
            tokenLabel,
            isDefaultToken: scope.isOperatorPayee,
            scope: {
                payee: scope.payee,
                sites: scope.sites,
                serviceTypes: scope.serviceTypes,
                dailyCap: scope.dailyCap,
                monthlyCap: scope.monthlyCap,
                expiresAt: scope.expiresAt,
            },
            effectiveCaps: defaultTerms ?? undefined,
        });
        if (decision.decision === 'deny') {
            return refuse(base, 'denied', 'user-denied');
        }
        const offered = preApproved ? [preApproved] : choices;
        chosen = offered.find(c => c.id === decision.walletId) ?? offered[0] ?? null;
        if (!chosen) return refuse(base, 'error', 'no-wallet-available');

        const policyEntry = {
            requesterId: request.requesterId,
            keyId: policyKeyIdForChoice(chosen),
            label: request.requesterLabel,
            purpose: BITCASH_AUTH_TOKEN_PURPOSE,
        };
        if (decision.decision === 'always') deps.grantAlways?.(cid, policyEntry);
        else if (decision.decision === 'session') deps.grantSession?.(cid, policyEntry);
    }

    // Gate 6: the cache — the default token only. A module's own token is
    // never cached and never served from one.
    const slot = scope.isOperatorPayee ? cacheSlotFor(cid, chosen) : null;
    const cache = deps.cache ?? null;
    if (slot && cache && defaultTerms) {
        if (request.previousTokenRejected === true) {
            // §4.12: the module says the token it held was refused — revoked,
            // expired, or outside its scope (`token_scope`, a type the node added
            // since). The cache is a cache; drop it and go to the ledger, which
            // repairs the token below.
            await safeClear(cache, slot);
        } else {
            const raw = await safeGet(cache, slot);
            const cached = decodeCachedToken(raw);
            if (cached && cachedTokenServes(cached, defaultTerms.serviceTypes)
                && (cached.expiresAt === null || cached.expiresAt > now())) {
                return await release(base, wrap, request.ephemeralPubkey, {
                    token: cached.token,
                    walletRef: cached.walletRef,
                    label: cached.label,
                    expiresAt: cached.expiresAt,
                });
            }
            // A token from before §9.3 (no scope recorded) or one the configured
            // list has outgrown: dropped, and refilled from the ledger.
            if (raw !== null) await safeClear(cache, slot);
        }
    }

    // Gate 7: the wallet key.
    let walletKey: string | null;
    if (chosen.kind === 'org') {
        if (!deps.getOrgWalletKey || !chosen.orgCid) {
            return refuse(base, 'error', 'org-wallet-unavailable');
        }
        walletKey = await deps.getOrgWalletKey(chosen.orgCid);
        if (!walletKey) return refuse(base, 'denied', 'org-wallet-key-unavailable');
    } else {
        if (!deps.getAccountWalletKey) return refuse(base, 'error', 'wallet-access-unavailable');
        walletKey = await deps.getAccountWalletKey(chosen);
        // The account's wallet document could not be read, or holds no ACTIVE
        // wallet in that slot. A refusal the member can act on (unlock the tab,
        // add a wallet in Settings), not a protocol error.
        if (!walletKey) return refuse(base, 'denied', 'wallet-key-unavailable');
    }

    // Gate 8: the default token — adopt, widen or replace it, or mint it; a
    // module's own — adopt or mint.
    let token: WorkerAuthToken;
    try {
        if (defaultTerms) {
            token = await settleDefaultToken(worker, walletKey, defaultTerms, now());
        } else {
            const existing = pickActiveToken(
                await worker.listAuthTokens(walletKey),
                tokenLabel,
                now(),
                verifyReceiveAddress(scope.payee) ? scope.payee : undefined,
            );
            token = existing ?? await worker.createAuthToken({
                walletKey,
                label: tokenLabel,
                sites: scope.sites,
                serviceTypes: scope.serviceTypes,
                dailyCap: caps.dailyCap,
                monthlyCap: caps.monthlyCap,
                expiresAt: scope.expiresAt,
                payee: scope.payee,
            });
        }
    } catch (err) {
        const message = err instanceof Error ? err.message.slice(0, 120) : 'worker-failed';
        return refuse(base, 'error', message);
    }

    if (!token.authToken) return refuse(base, 'error', 'worker-returned-no-token');

    const walletRef = await worker.getWalletRef(walletKey) ?? undefined;

    // Gate 9: the cache records the scope the token now has.
    if (slot && cache && defaultTerms) {
        await safeSet(cache, slot, encodeCachedToken({
            token: token.authToken,
            walletRef,
            label: tokenLabel,
            expiresAt: token.expiresAt ?? null,
            serviceTypes: token.serviceTypes,
            unbound: true,
        }));
    }

    return await release(base, wrap, request.ephemeralPubkey, {
        token: token.authToken,
        walletRef,
        label: token.label ?? tokenLabel,
        expiresAt: token.expiresAt ?? null,
    });
}

/** The cache slot for one wallet, or null where the plan defines none — a
 *  second account wallet the member picked deliberately is served from the
 *  Worker every time rather than from a slot nobody specified. */
function cacheSlotFor(cid: string, choice: BitcashWalletChoice): string | null {
    if (choice.kind === 'org') {
        return choice.orgCid ? orgDefaultTokenSlot(cid, choice.orgCid) : null;
    }
    return (choice.keyIndex ?? 0) === 0 ? defaultTokenSlot(cid) : null;
}

/**
 * The wallet's default token, made to match the node's terms (§2.3 item 4): adopted when it is
 * unbound and covers the configured service types; widened in place when it lacks one — free, and
 * the modules keep the code they hold (`token/update` replaces the caps whole, so day, month and
 * types go together); revoked and replaced when it is still bound to a receive address, which
 * `token/update` cannot remove (one `fee:auth`); minted when there is none.
 */
async function settleDefaultToken(
    worker: BitcashWorkerApi, walletKey: string, terms: DefaultTokenTerms, now: number,
): Promise<WorkerAuthToken> {
    const plan = planDefaultToken(await worker.listAuthTokens(walletKey), terms.serviceTypes, now);
    switch (plan.action) {
        case 'adopt':
            return plan.token;
        case 'widen':
            await worker.updateAuthToken(walletKey, plan.token.authToken, terms);
            return {
                ...plan.token, serviceTypes: [...terms.serviceTypes],
                dailyCap: terms.dailyCap, monthlyCap: terms.monthlyCap, expiresAt: null,
            };
        case 'replace':
            for (const bound of plan.revoke) await worker.revokeAuthToken(walletKey, bound.authToken);
            break;
        case 'mint':
            break;
    }
    return await worker.createAuthToken({
        walletKey,
        label: DEFAULT_TOKEN_LABEL,
        payee: 'operator',
        sites: [],
        serviceTypes: [...terms.serviceTypes],
        dailyCap: terms.dailyCap,
        monthlyCap: terms.monthlyCap,
        expiresAt: null,
    });
}

/**
 * The default token's terms, from `GET /api/registration/config` → `defaultToken`: D27's caps in
 * BIT and the service types (§9.3 D-a) — go-live configuration (apps/bitcash/docs/plans/bitcash-v2.md
 * M12, D15 ruling). Null when the node names none, or they cannot be read, or the list is empty or
 * unusable — no number or list stands in for them, and an unbound token is never minted unscoped.
 */
async function defaultTokenTerms(deps: BitcashDispatchDeps): Promise<DefaultTokenTerms | null> {
    if (!deps.getDefaultTokenCaps) return null;
    try {
        const terms = await deps.getDefaultTokenCaps();
        if (terms === null) return null;
        const usable = (v: number) => Number.isSafeInteger(v) && v > 0;
        if (!usable(terms.dailyCap) || !usable(terms.monthlyCap) || terms.monthlyCap < terms.dailyCap) return null;
        const types = terms.serviceTypes;
        if (!Array.isArray(types) || types.length === 0 || types.length > MAX_SCOPE_LIST || !areServiceTypes(types)) return null;
        return { dailyCap: terms.dailyCap, monthlyCap: terms.monthlyCap, serviceTypes: [...types] };
    } catch {
        return null;
    }
}

async function release(
    base: ResponseBase,
    wrap: NonNullable<BitcashDispatchDeps['wrapForRequester']>,
    ephemeralPubkey: string,
    payload: { token: string; walletRef?: string; label: string; expiresAt: number | null },
): Promise<BitcashAuthTokenResponse> {
    try {
        const wrapped = await wrap(ephemeralPubkey, new TextEncoder().encode(payload.token));
        return {
            ...base,
            status: 'granted',
            wrapped,
            // Mirrored where a consumer built on the shipped `KeyGrantClient`
            // unwrap path already looks.
            cidEphemeralPubkey: wrapped.cidEphemeralPubkey,
            nonce: wrapped.nonce,
            ciphertext: wrapped.ciphertext,
            walletRef: payload.walletRef,
            label: payload.label,
            expiresAt: payload.expiresAt === null ? null : new Date(payload.expiresAt).toISOString(),
        };
    } catch (err) {
        const message = err instanceof Error ? err.message.slice(0, 120) : 'wrap-failed';
        return refuse(base, 'error', message);
    }
}

async function safeGet(cache: NonNullable<BitcashDispatchDeps['cache']>, slot: string): Promise<string | null> {
    try {
        return await cache.get(slot);
    } catch {
        return null;
    }
}

async function safeSet(
    cache: NonNullable<BitcashDispatchDeps['cache']>, slot: string, value: string,
): Promise<void> {
    try {
        await cache.set(slot, value);
    } catch {
        // A cache that cannot be written costs a prompt next time, nothing more.
    }
}

async function safeClear(
    cache: NonNullable<BitcashDispatchDeps['cache']>, slot: string,
): Promise<void> {
    try {
        await cache.clear(slot);
    } catch {
        // Same: a stale slot is re-checked against the Worker on the next hit.
    }
}
